import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, request } from '@/api/client';

interface Resource<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Load something from the API and keep it in step with its inputs.
 *
 * Every request is aborted when its inputs change or the screen unmounts, so a
 * slow response for a filter the user has already moved past cannot arrive
 * late and overwrite the current one.
 *
 * `deps` is the dependency list, same contract as useEffect.
 */
export function useResource<T>(
  path: string,
  query:
    | Record<string, string | number | boolean | null | undefined>
    | undefined,
  deps: unknown[],
): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const requestKey = JSON.stringify([path, query]);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const [settledNonce, setSettledNonce] = useState<number | null>(null);
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    const refresh = () => setNonce((value) => value + 1);
    window.addEventListener('admin-data-purpose-changed', refresh);
    return () => window.removeEventListener('admin-data-purpose-changed', refresh);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    request<T>(path, { query: queryRef.current, signal: controller.signal })
      .then((result) => {
        if (controller.signal.aborted) return;
        setSettledKey(requestKey);
        setSettledNonce(nonce);
        setData(result);
        setLoading(false);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        // A 401 is already taking the user to the sign-in screen; showing an
        // error under it would only be noise.
        if (caught instanceof ApiError && caught.status === 401) return;
        setSettledKey(requestKey);
        setSettledNonce(nonce);
        setData(null);
        setError(caught instanceof Error ? caught.message : String(caught));
        setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, requestKey, nonce, ...deps]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  // Never paint one cohort under another cohort’s freshly changed filters.
  const current = settledKey === requestKey && settledNonce === nonce;
  return {
    data: current ? data : null,
    error: current ? error : null,
    loading: !current || loading,
    reload,
  };
}

/**
 * Hold a value back until the user stops typing.
 *
 * Straight through to the API on every keystroke would fire a query per
 * character and paint results the user has already typed past.
 */
export function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Sorting state shared by every table: click once to sort, again to flip. */
export function useSort(
  initialKey: string,
  initialOrder: 'asc' | 'desc' = 'desc',
) {
  const [sort, setSort] = useState(initialKey);
  const [order, setOrder] = useState<'asc' | 'desc'>(initialOrder);

  const toggle = useCallback(
    (key: string) => {
      setOrder((currentOrder) =>
        key === sort ? (currentOrder === 'asc' ? 'desc' : 'asc') : 'asc',
      );
      setSort(key);
    },
    [sort],
  );

  return { sort, order, toggle };
}
